import React, {useEffect, useRef} from "react";
import ReactDOM from "react-dom";
import SearchWithAiButton from "../SearchWithAiButton/SearchWithAiButton";
import {EntryPoint} from "../Chatbot/EntryPoint";
import NobiEventTracker from "../../utils/NobiEventTracker";
import Events from "../../utils/Events";

const ShopWithAiButtonInjector = ({
    merchantId,
    assistantConfiguration,
    brandTheme,
    onClick,
    log
}) => {
    const containersRef = useRef([]);

    const selector = assistantConfiguration?.shopWithAiButton?.containerSelector;

    useEffect(() => {
        if (!selector) {
            log && log("No search with AI button container selector provided in assistant configuration. Aborting ShopWithAiButtonInjector.");
            return;
        }

        // already injected
        if (containersRef.current.length) {
            return;
        }

        // Find the container element using the provided selector
        const parentContainers = document.querySelectorAll(selector);
        if (parentContainers.length === 0) {
            log?.(`No elements found for selector ${selector}`);
            return;
        }

        const created = [];
        parentContainers.forEach(parentContainer => {
            injectButtonAndRecordContainer(parentContainer, created);
        });
        containersRef.current = created;

        setTimeout(() => {
            applyOverridesForPreexistingElements();
        });

        return cleanupButtonsCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selector]);

    function createButtonContainer(parentContainer) {
        let buttonContainer = document.createElement("div");
        if (parentContainer.tagName === "UL" || parentContainer.tagName === "OL") {
            log && log("Creating list item for button container");
            buttonContainer = document.createElement("li");
        }
        else {
            log && log("Creating div for button container");
            buttonContainer = document.createElement("div");
        }
        return buttonContainer;
    }

    function renderButtonIntoContainer(parentContainer, buttonContainer) {
        parentContainer.appendChild(buttonContainer);
        ReactDOM.render(
            <SearchWithAiButton
                merchantId={merchantId}
                assistantConfiguration={assistantConfiguration}
                brandTheme={brandTheme}
                onClick={onClick}
                buttonLabel={assistantConfiguration?.shopWithAiButton?.buttonLabel || "Shop With AI"}
                log={log}
                isTextButton={assistantConfiguration?.shopWithAiButton?.isTextButton}
                additionalClassNames={assistantConfiguration?.shopWithAiButton?.additionalClassNames}
                iconUrl={assistantConfiguration?.shopWithAiButton?.iconUrl}
                entryPoint={EntryPoint.ShopWithAiButton}
            />,
            buttonContainer
        );

        NobiEventTracker.trackEvent(
            merchantId,
            {},
            EntryPoint.ShopWithAiButton,
            Events.ShopWithAiButtonShown
        );
    }

    function injectButtonAndRecordContainer(parentContainer, created) {
        log && log("Creating button container");
        let buttonContainer = createButtonContainer(parentContainer);
        renderButtonIntoContainer(parentContainer, buttonContainer);
        created.push(buttonContainer);
    }

    function cleanupButtonsCallback() {
        return () => {
            containersRef.current && containersRef.current.forEach(node => {
                ReactDOM.unmountComponentAtNode(node);
                node.remove();
            });
            containersRef.current = [];
        };
    }

    function applyOverridesForPreexistingElements() {
        if (!brandTheme?.preexistingElementOverrides?.shopWithAiButton) {
            log && log("No preexisting element overrides for shopWithAiButton found in brand theme.");
            return;
        }

        Object.entries(brandTheme?.preexistingElementOverrides?.shopWithAiButton).forEach(([selector, styles]) => {
            const elements = document.querySelectorAll(selector);
            if (elements.length === 0) {
                log && log(`No elements found for selector ${selector}`);
                return;
            }

            elements.forEach(element => {
                Object.entries(styles).forEach(([property, value]) => {
                    element.style[property] = value;
                });
                log && log(`Applied styles to ${selector}: ${JSON.stringify(styles)}`);
            });
        });
    }

    return null;
};

export default ShopWithAiButtonInjector;
